/**
 * MIRRORED FILE — edit shared/ version and re-copy to web-client/src/utils/;
 * sync enforced by test in shared/utils/__tests__/getAppointmentBucket.test.ts.
 *
 * Single source of truth for appointment tab bucketing, sorting direction,
 * and the canonical bucket list.
 *
 * Tabs (left to right): Pending | Confirmed | Past
 *
 *   Pending   – awaiting stylist action: 'pending'
 *   Confirmed – stylist has accepted; service is active or upcoming:
 *               'confirmed', 'checked-in', 'in-progress'
 *               EXCEPTION: if derived endTime is already in the past → Past
 *   Past      – terminal states or time-expired confirmed appointments:
 *               'completed', 'cancelled', 'no-show', 'expired', 'rescheduled',
 *               and any Confirmed-bucket appointment whose endTime < now
 *
 * Sorting contract — enforced via getSortDirection():
 *   Pending   → 'asc'  (soonest first)
 *   Confirmed → 'asc'  (soonest first)
 *   Past      → 'desc' (most recent first)
 *
 * ─────────────────────────────────────────────────────────────────
 * IMPORTANT – CRA restriction:
 * web-client (Create React App) cannot import from outside src/.
 * Mirror this file at web-client/src/utils/getAppointmentBucket.ts
 * and keep the two copies byte-for-byte identical.
 * Run `npm run check-appointment-bucket-parity` from the repo root
 * to detect drift; the sync guard test in the __tests__ file catches
 * it automatically in the Jest run.
 *
 * Deferred: replace the mirror with a @shared alias once we migrate
 * off CRA (CRACO or Vite). Tracked in GitHub Backlog.
 * ─────────────────────────────────────────────────────────────────
 */

export type AppointmentBucket = 'pending' | 'confirmed' | 'past';

/**
 * Tab order (left to right). Import this in all views instead of
 * declaring a local ['pending','confirmed','past'] array — prevents
 * the list from silently drifting between platforms.
 */
export const APPOINTMENT_BUCKETS: readonly AppointmentBucket[] = [
  'pending',
  'confirmed',
  'past',
];

/**
 * Minimal appointment shape required by getAppointmentEndTime.
 * Both platform Appointment types satisfy this interface.
 */
export interface AppointmentWithTimes {
  dateTime: string;
  endTime?: string | null;
  service?: { duration?: number | null } | null;
}

/**
 * Returns the ISO end-time string for an appointment.
 *
 * Resolution order:
 *   1. appointment.endTime  — field populated by the backend (preferred).
 *      NOTE: mobile's Appointment.endTime is a required string, so mobile
 *      always takes this path. Paths 2 & 3 are fallbacks for web/legacy
 *      API responses where endTime may be absent.
 *   2. dateTime + service.duration (minutes) — duration-aware fallback.
 *   3. dateTime + 2 hours — last-resort buffer for legacy records without
 *      endTime or duration.
 *      TODO: Remove 2 h buffer once all legacy appointments are backfilled.
 *            Tracked in GitHub Backlog.
 *
 * Use this at every call site so derivation logic lives in one place:
 *   getAppointmentBucket(apt.status, getAppointmentEndTime(apt))
 */
export function getAppointmentEndTime(apt: AppointmentWithTimes): string {
  if (apt.endTime) return apt.endTime;
  const startMs = new Date(apt.dateTime).getTime();
  const durationMs = apt.service?.duration
    ? apt.service.duration * 60_000
    : 2 * 60 * 60_000; // TODO: 2 h fallback; remove when endTime always present
  return new Date(startMs + durationMs).toISOString();
}

/**
 * Maps a raw appointment status string (and derived end time) to one of
 * three display buckets: 'pending' | 'confirmed' | 'past'.
 *
 * @param status  Raw appointment.status string from the backend.
 *                API returns a flat string (not a nested object).
 * @param endTime ISO string for the appointment's end time.
 *                Pass getAppointmentEndTime(apt) here — do not inline the
 *                derivation at each call site.
 */
export function getAppointmentBucket(
  status: string,
  endTime?: string | null
): AppointmentBucket {
  switch (status) {
    case 'pending':
      return 'pending';

    case 'confirmed':
    case 'checked-in':
    case 'in-progress': {
      if (endTime) {
        const end = new Date(endTime);
        if (!isNaN(end.getTime()) && end < new Date()) {
          return 'past';
        }
      }
      return 'confirmed';
    }

    case 'completed':
    case 'cancelled':
    case 'no-show':
    case 'expired':
    case 'rescheduled':
    default:
      return 'past';
  }
}

/**
 * Returns the sort direction for a given appointment bucket.
 *   pending   → 'asc'  (soonest appointment shown first)
 *   confirmed → 'asc'  (soonest appointment shown first)
 *   past      → 'desc' (most recent appointment shown first)
 *
 * Usage in views:
 *   const dir = getSortDirection(currentBucket);
 *   list.sort((a, b) => {
 *     const diff = new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
 *     return dir === 'asc' ? diff : -diff;
 *   });
 */
export function getSortDirection(bucket: AppointmentBucket): 'asc' | 'desc' {
  return bucket === 'past' ? 'desc' : 'asc';
}

/**
 * Eligibility for Home / dashboard "Upcoming Appointment" widgets.
 * Matches: not in the Past bucket AND start time still in the future.
 *
 * Use this (not a hand-rolled status allowlist) so mobile and web stay aligned
 * with Appointments-tab Past semantics from getAppointmentBucket.
 */
export function isUpcomingHomeAppointment(
  apt: AppointmentWithTimes & { status?: string | null },
  now: Date = new Date()
): boolean {
  if (!apt?.dateTime || !apt.status) return false;
  if (getAppointmentBucket(apt.status, getAppointmentEndTime(apt)) === 'past') {
    return false;
  }
  const aptDate = new Date(apt.dateTime);
  if (isNaN(aptDate.getTime())) return false;
  return aptDate > now;
}

/**
 * Soonest upcoming home appointments, sorted ascending by dateTime.
 * Default limit 1 matches the singular "Upcoming Appointment" card.
 */
export function selectUpcomingHomeAppointments<T extends AppointmentWithTimes & { status?: string | null }>(
  appointments: T[],
  options?: { now?: Date; limit?: number }
): T[] {
  const now = options?.now ?? new Date();
  const limit = options?.limit ?? 1;
  return appointments
    .filter((apt) => isUpcomingHomeAppointment(apt, now))
    .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime())
    .slice(0, limit);
}
